import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

const VIEWPORT_MARGIN = 8;

/** One flyout at a time beside the rail: opening, viewport fitting and every way it closes. */
export function useRailFlyout() {
    const [openKey, setOpenKey] = useState<string | null>(null);
    const [anchorTop, setAnchorTop] = useState(0);
    const [flyoutTop, setFlyoutTop] = useState(0);
    const [flyoutMaxHeight, setFlyoutMaxHeight] = useState(0);
    const [panel, setPanel] = useState<HTMLDivElement | null>(null);
    const triggers = useRef<Record<string, HTMLButtonElement | null>>({});
    const { pathname } = useLocation();

    const close = useCallback(
        (options?: { returnFocus?: boolean }) => {
            if (options?.returnFocus && openKey) triggers.current[openKey]?.focus();
            setOpenKey(null);
        },
        [openKey]
    );

    const toggle = useCallback(
        (key: string) => {
            if (openKey === key) {
                setOpenKey(null);
                return;
            }
            setAnchorTop(triggers.current[key]?.getBoundingClientRect().top ?? 0);
            setFlyoutMaxHeight(window.innerHeight - VIEWPORT_MARGIN * 2);
            setOpenKey(key);
        },
        [openKey]
    );

    useLayoutEffect(() => {
        if (!openKey || !panel) return;
        setFlyoutTop(Math.min(anchorTop, window.innerHeight - panel.offsetHeight - VIEWPORT_MARGIN));
    }, [openKey, panel, anchorTop]);

    useEffect(() => {
        setOpenKey(null);
    }, [pathname]);

    useEffect(() => {
        if (!openKey) return;
        const closeOnOutsidePointer = (event: PointerEvent) => {
            const target = event.target as Node;
            if (triggers.current[openKey]?.contains(target) || panel?.contains(target)) return;
            setOpenKey(null);
        };
        document.addEventListener('pointerdown', closeOnOutsidePointer);
        return () => document.removeEventListener('pointerdown', closeOnOutsidePointer);
    }, [openKey, panel]);

    const triggerRef = useCallback(
        (key: string) => (el: HTMLButtonElement | null) => {
            triggers.current[key] = el;
        },
        []
    );

    const itemHandlers = useCallback(
        (key: string) => ({
            onKeyDown: (event: React.KeyboardEvent) => {
                if (event.key !== 'Escape' || openKey !== key) return;
                event.stopPropagation();
                close({ returnFocus: true });
            },
            onBlur: (event: React.FocusEvent) => {
                const next = event.relatedTarget as Node | null;
                if (openKey === key && next && !event.currentTarget.contains(next)) setOpenKey(null);
            },
        }),
        [openKey, close]
    );

    return {
        openKey,
        toggle,
        close,
        triggerRef,
        flyoutTop,
        flyoutMaxHeight,
        panelRef: setPanel,
        itemHandlers,
    };
}
